import type { Locale } from "@llmgateway/i18n";

const en = {
	activityLogs: {
		title: "Recent Activity",
		description: "Real-time request logs with detailed metadata",
		logs: [
			"doteb is a unified API for accessing multiple AI models. It provides intelligent routing, cost optimization, and comprehensive analytics.",
			"You can integrate doteb using the OpenAI SDK. Just change the base URL and API key.",
			"",
		],
		error: {
			statusText: "Too Many Requests",
			responseText: "Rate limit exceeded. Please try again later.",
		},
	},
	apiKey: {
		title: "API Key Management",
		description: "Securely manage and monitor your API keys",
		status: {
			active: "active",
		},
		names: {
			"Production API Key": "Production API Key",
			"Development Key": "Development Key",
			"Testing Environment": "Testing Environment",
		},
		requests: "requests",
		lastUsed: "Last used",
		secureStorage: "Secure Key Storage",
		secureStorageDescription:
			"All API keys are encrypted at rest and in transit. Keys are only shown once during creation and cannot be retrieved later.",
	},
	costAnalytics: {
		totalSpend: "Total Spend",
		last7Days: "Last 7 days",
		avgCostPer1KTokens: "Avg Cost per 1K Tokens",
		blendedRate: "Blended rate",
		totalTokens: "Total Tokens",
		inputOutput: "Input + Output",
		costByProvider: "Cost by Provider",
		costByProviderDescription: "Distribution of spending across providers",
		providerBreakdown: "Provider Breakdown",
		providerBreakdownDescription: "Detailed cost analysis by provider",
		ofTotal: "of total",
	},
	errorsMonitoring: {
		errorRate: "Error Rate",
		errorRateDescription: "Track reliability and identify issues early",
		failedRequests: "Failed Requests",
		successfulRequests: "Successful Requests",
		totalRequests: "Total Requests",
		cachePerformance: "Cache Performance",
		cachePerformanceDescription: "Monitor cache efficiency and cost savings",
		cacheHitRate: "Cache Hit Rate",
		cachedResponses: "Cached Responses",
		estimatedSavings: "Estimated Savings",
		cacheEfficiency: "Cache Efficiency",
		excellent: "Excellent",
		reliabilitySummary: "Reliability Summary",
		reliabilitySummaryDescription: "Overall system health metrics",
		successRate: "Success Rate",
		avgLatency: "Avg Latency",
		criticalErrors: "Critical Errors",
		uptime: "Uptime",
	},
	modelBreakdown: {
		title: "Model Usage Breakdown",
		description: "Detailed performance and cost metrics by model",
		model: "Model",
		provider: "Provider",
		requests: "Requests",
		tokens: "Tokens",
		cost: "Cost",
		avgLatency: "Avg Latency",
		totalModels: "Total Models",
		totalRequests: "Total Requests",
		totalCost: "Total Cost",
	},
	multiProvider: {
		title: "Connected Providers",
		description: "Access 100+ models from 19+ providers through a single API",
		models: "models",
		active: "active",
		avgLatency: "Avg Latency",
		totalSpend: "Total Spend",
		providerIntegrations: "Provider Integrations",
		availableModels: "Available Models",
		unifiedApi: "Unified API",
	},
	performanceMonitoring: {
		totalRequests: "Total Requests",
		last7Days: "Last 7 days",
		avgLatency: "Avg Latency",
		meanResponseTime: "Mean response time",
		cacheHitRate: "Cache Hit Rate",
		cachedResponses: "Cached responses",
		requestActivity: "Request Activity",
		requestActivityDescription: "Daily request volume over the last 7 days",
		requests: "requests",
		dateFormat: "MMM d",
	},
};

const zhCN = {
	activityLogs: {
		title: "最近活动",
		description: "带详细元数据的实时请求日志",
		logs: [
			"doteb 是用于访问多个 AI 模型的统一 API，提供智能路由、成本优化和完整分析。",
			"你可以使用 OpenAI SDK 集成 doteb，只需修改 base URL 和 API key。",
			"",
		],
		error: {
			statusText: "请求过多",
			responseText: "已达到速率限制，请稍后重试。",
		},
	},
	apiKey: {
		title: "API 密钥管理",
		description: "安全管理并监控你的 API 密钥",
		status: {
			active: "启用",
		},
		names: {
			"Production API Key": "生产环境 API 密钥",
			"Development Key": "开发环境密钥",
			"Testing Environment": "测试环境密钥",
		},
		requests: "次请求",
		lastUsed: "最近使用",
		secureStorage: "安全密钥存储",
		secureStorageDescription:
			"所有 API 密钥在静态存储和传输过程中都会加密。密钥仅在创建时显示一次，之后无法再次取回。",
	},
	costAnalytics: {
		totalSpend: "总支出",
		last7Days: "最近 7 天",
		avgCostPer1KTokens: "每 1K Tokens 平均成本",
		blendedRate: "混合费率",
		totalTokens: "总 Tokens",
		inputOutput: "输入 + 输出",
		costByProvider: "按供应商统计成本",
		costByProviderDescription: "各供应商支出占比分布",
		providerBreakdown: "供应商明细",
		providerBreakdownDescription: "按供应商查看详细成本分析",
		ofTotal: "占总额",
	},
	errorsMonitoring: {
		errorRate: "错误率",
		errorRateDescription: "追踪可靠性并尽早发现问题",
		failedRequests: "失败请求",
		successfulRequests: "成功请求",
		totalRequests: "总请求数",
		cachePerformance: "缓存表现",
		cachePerformanceDescription: "监控缓存效率和成本节省",
		cacheHitRate: "缓存命中率",
		cachedResponses: "缓存响应",
		estimatedSavings: "预计节省",
		cacheEfficiency: "缓存效率",
		excellent: "优秀",
		reliabilitySummary: "可靠性概览",
		reliabilitySummaryDescription: "整体系统健康指标",
		successRate: "成功率",
		avgLatency: "平均延迟",
		criticalErrors: "严重错误",
		uptime: "可用率",
	},
	modelBreakdown: {
		title: "模型使用明细",
		description: "按模型查看详细性能和成本指标",
		model: "模型",
		provider: "供应商",
		requests: "请求",
		tokens: "Tokens",
		cost: "成本",
		avgLatency: "平均延迟",
		totalModels: "模型总数",
		totalRequests: "总请求数",
		totalCost: "总成本",
	},
	multiProvider: {
		title: "已连接供应商",
		description: "通过单一 API 访问 19+ 供应商的 100+ 模型",
		models: "个模型",
		active: "启用",
		avgLatency: "平均延迟",
		totalSpend: "总支出",
		providerIntegrations: "供应商集成",
		availableModels: "可用模型",
		unifiedApi: "统一 API",
	},
	performanceMonitoring: {
		totalRequests: "总请求数",
		last7Days: "最近 7 天",
		avgLatency: "平均延迟",
		meanResponseTime: "平均响应时间",
		cacheHitRate: "缓存命中率",
		cachedResponses: "缓存响应",
		requestActivity: "请求活动",
		requestActivityDescription: "最近 7 天的每日请求量",
		requests: "次请求",
		dateFormat: "M月d日",
	},
} satisfies typeof en;

export function getFeatureDemoCopy(locale: Locale) {
	return locale === "zh-CN" ? zhCN : en;
}
